import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchNews, fetchSiteSettings, fetchMenuItems } from '../lib/api.js';

// Fallbacks — only used while the API call is in flight or returns nothing.
const DEFAULTS = {
  schoolName: 'Maple Leaf',
  brandTag: 'International School · Est. 1972',
  logo: '/img/mlis-logo.jpg',
  contactEmail: 'info@mlis.edu.bd',
  contactPhone: '+880 1810 199272',
};

// Hardcoded fallback nav — used only if /api/menu-items is empty (e.g. fresh
// install without seed). Once the admin has MenuItem rows, this is dead code.
const FALLBACK_NAV = [
  { id: 'h-home', label: 'Home', url: '/' },
  { id: 'h-admission', label: 'Admission', url: '/admission' },
  { id: 'h-contact', label: 'Contact', url: '/contact' },
];
const FALLBACK_TOPBAR = [
  { id: 't-s', label: 'Student Login',    url: 'https://mlis.education/student/log_in',    openInNewTab: true },
  { id: 't-t', label: 'Teacher Login',    url: 'https://mlis.education/teacher/log_in',    openInNewTab: true },
  { id: 't-u', label: 'Supervisor Login', url: 'https://mlis.education/supervisor/log_id', openInNewTab: true },
];

const TICKER_FALLBACK = [
  { tag: 'Admission', text: 'Admissions open for 2026 — apply for PG, KG and Class I', href: '/admission' },
  { tag: 'News', text: 'Maple Leaf wins Inter-School Debate Championship 2025', href: '/news' },
  { tag: 'Event', text: 'Annual Sports Day rescheduled to 18 February', href: '/events' },
];

const isInternalUrl = (u) => !!u && u.startsWith('/');
const isDropdownTrigger = (item) => !item.url || item.url === '#';

/** Build a parent → children tree out of the flat header items. */
function buildHeaderTree(headerItems) {
  const byId = new Map(headerItems.map((i) => [i.id, { ...i, children: [] }]));
  const roots = [];
  for (const i of headerItems) {
    if (i.parentId && byId.has(i.parentId)) {
      byId.get(i.parentId).children.push(byId.get(i.id));
    } else {
      roots.push(byId.get(i.id));
    }
  }
  const bySort = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  roots.sort(bySort);
  roots.forEach((r) => r.children.sort(bySort));
  return roots;
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const location = useLocation();

  const { data: settings } = useApi(fetchSiteSettings, []);
  const { data: menu } = useApi(fetchMenuItems, []);

  const schoolName = settings?.schoolName || DEFAULTS.schoolName;
  const brandTag = settings?.brandTag || DEFAULTS.brandTag;
  const logo = settings?.logo || DEFAULTS.logo;
  const contactEmail = settings?.contactEmail || DEFAULTS.contactEmail;
  const contactPhone = settings?.contactPhone || DEFAULTS.contactPhone;

  const headerItems = useMemo(() => {
    const arr = menu?.header || [];
    return arr.length ? buildHeaderTree(arr) : FALLBACK_NAV;
  }, [menu]);

  const topbarItems = menu?.topbar_right?.length ? menu.topbar_right : FALLBACK_TOPBAR;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenItem(null); }, [location.pathname]);

  return (
    <>
      <div className="topbar">
        <div className="container">
          <div className="topbar-left">
            <span>Have a question?</span>
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            <span className="sep">·</span>
            <a href={`tel:${contactPhone.replace(/[^+\d]/g, '')}`}>{contactPhone}</a>
          </div>
          <div className="topbar-right">
            {topbarItems.map((it, i) => (
              <span key={it.id}>
                {i > 0 && <span className="sep">·</span>}
                {isInternalUrl(it.url) ? (
                  <Link to={it.url}>{it.label}</Link>
                ) : (
                  <a
                    href={it.url}
                    target={it.openInNewTab ? '_blank' : undefined}
                    rel={it.openInNewTab ? 'noopener noreferrer' : undefined}
                  >
                    {it.label}
                  </a>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      <header className={`site-header${scrolled ? ' scrolled' : ''}`}>
        <div className="container">
          <Link to="/" className="brand">
            <div className="brand-mark">
              <img src={logo} alt={schoolName} width="48" height="48" decoding="async" fetchpriority="high" />
            </div>
            <div className="brand-text">
              <span className="brand-name">{schoolName}</span>
              <span className="brand-tag">{brandTag}</span>
            </div>
          </Link>

          <button
            className="menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <nav className={`nav${mobileOpen ? ' open' : ''}`}>
            {headerItems.map((item, idx) => (
              <NavItem
                key={item.id}
                item={item}
                isOpen={openItem === idx}
                onToggle={() => setOpenItem(openItem === idx ? null : idx)}
              />
            ))}
          </nav>
        </div>
      </header>

      <NewsTicker />
    </>
  );
}

function NavItem({ item, isOpen, onToggle }) {
  const hasChildren = item.children && item.children.length > 0;
  const trigger = isDropdownTrigger(item);

  return (
    <div className={`nav-item${isOpen ? ' expanded' : ''}`}>
      {trigger ? (
        <a
          className="nav-link"
          href="#"
          onClick={(e) => { e.preventDefault(); onToggle(); }}
        >
          {item.label}{hasChildren && <span className="caret" />}
        </a>
      ) : isInternalUrl(item.url) ? (
        <NavLink
          to={item.url}
          end={item.url === '/'}
          className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
          onClick={(e) => {
            // On mobile a parent-link-with-children opens the dropdown
            // instead of navigating.
            if (hasChildren && window.innerWidth <= 860) {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          {item.label}{hasChildren && <span className="caret" />}
        </NavLink>
      ) : (
        <a
          className="nav-link"
          href={item.url}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
        >
          {item.label}{hasChildren && <span className="caret" />}
        </a>
      )}

      {hasChildren && (
        <div className="dropdown">
          {item.sectionTitle && <div className="dropdown-title">{item.sectionTitle}</div>}
          {item.children.map((child) => (
            isInternalUrl(child.url) ? (
              <Link key={child.id} to={child.url}>{child.label}</Link>
            ) : (
              <a
                key={child.id}
                href={child.url}
                target={child.openInNewTab ? '_blank' : undefined}
                rel={child.openInNewTab ? 'noopener noreferrer' : undefined}
              >
                {child.label}
              </a>
            )
          ))}
        </div>
      )}
    </div>
  );
}

const CAT_TAG = {
  notice: 'Notice', story: 'Story', event: 'Event',
  achievement: 'Achievement', in_memoriam: 'In Memoriam',
};

function NewsTicker() {
  const { data } = useApi(() => fetchNews({ limit: 6 }), []);
  const apiItems = (data?.items || []).map((n) => ({
    tag: CAT_TAG[n.category] || 'News',
    text: n.title,
    href: `/news/${n.slug}`,
  }));
  const items = apiItems.length ? apiItems : TICKER_FALLBACK;

  return (
    <div className="news-ticker">
      <div className="label">Latest</div>
      <div className="track">
        {[0, 1].map((dup) => (
          <div className="track-inner" key={dup} aria-hidden={dup === 1}>
            {items.map((it, i) => (
              <span key={i}>
                <Link to={it.href}><span className="tag">{it.tag}</span> {it.text}</Link>
                {i < items.length - 1 && <span className="sep" />}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
